/**
 * 文章标注的 DOM 工具。
 *
 * 锚点模型：`段落 id + 句序 + 句内字符偏移`。
 * 之所以不用「文档绝对字符偏移」，是因为段落与句序在题源重新导入后仍可对上，
 * 最多退化为整句高亮，而绝对偏移会整体错位（见 docs/PLAN.md 3.5 不变量 6）。
 *
 * 本文件只在浏览器里运行。
 */
const BLOCK_SELECTOR = '[data-block-id]';
export const SENTENCE_SELECTOR = '.sentence';
let diagnosticSink = null;
export function setAnnotationDiagnostics(sink) {
    diagnosticSink = sink;
}
function report(event) {
    diagnosticSink?.(event);
}
/** 把一段 DOM 的文本建成索引，用于在「归一化文本坐标」与「DOM 位置」之间互转。 */
export function buildTextIndex(root) {
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
    const nodes = [];
    const globalRawToNorm = [];
    let text = '';
    let rawCursor = 0;
    let current = walker.nextNode();
    while (current) {
        const raw = current.data;
        const rawToNorm = new Int32Array(raw.length);
        rawToNorm.fill(-1);
        const rawStart = rawCursor;
        const normStart = text.length;
        let lastWasSpace = text.length > 0 && text.endsWith(' ');
        for (let i = 0; i < raw.length; i += 1) {
            const char = raw[i];
            if (/\s/.test(char)) {
                if (!lastWasSpace && text.length > 0) {
                    rawToNorm[i] = text.length;
                    text += ' ';
                    lastWasSpace = true;
                }
            }
            else {
                rawToNorm[i] = text.length;
                text += char;
                lastWasSpace = false;
            }
        }
        for (let i = 0; i < raw.length; i += 1) {
            globalRawToNorm.push(rawToNorm[i]);
        }
        nodes.push({
            node: current,
            rawLength: raw.length,
            rawStart,
            normStart,
            normEnd: text.length,
            rawToNorm,
        });
        rawCursor += raw.length;
        current = walker.nextNode();
    }
    if (text.endsWith(' '))
        text = text.slice(0, -1);
    return { root, text, nodes, rawToNorm: Int32Array.from(globalRawToNorm) };
}
/** 用 Range 量出「从索引根节点到某个 DOM 边界」的原始文本长度。 */
function rawBoundaryOf(index, container, offset) {
    const probe = document.createRange();
    try {
        probe.setStart(index.root, 0);
        probe.setEnd(container, offset);
    }
    catch {
        return 0;
    }
    return probe.toString().length;
}
/**
 * DOM 位置（节点 + 偏移）转归一化偏移。
 *
 * 关键在于**元素容器也要能算**：三击选段、键盘选词、`selectNodeContents`
 * 给出的边界容器都是元素而不是文本节点。这里统一用 Range 量原始长度再映射，
 * 因此两类容器走同一条路径。
 */
export function normOffsetOf(index, node, offset) {
    const rawBoundary = rawBoundaryOf(index, node, offset);
    const limit = Math.min(rawBoundary, index.rawToNorm.length);
    let last = -1;
    for (let i = 0; i < limit; i += 1) {
        const value = index.rawToNorm[i];
        if (value >= 0)
            last = value;
    }
    if (last >= 0)
        return Math.min(last + 1, index.text.length);
    for (let i = limit; i < index.rawToNorm.length; i += 1) {
        const value = index.rawToNorm[i];
        if (value >= 0)
            return value;
    }
    return index.text.length;
}
/** 归一化偏移转 DOM 位置。 */
export function positionAt(index, normOffset) {
    if (index.nodes.length === 0)
        return null;
    const target = Math.max(0, Math.min(normOffset, index.text.length));
    for (const entry of index.nodes) {
        if (target < entry.normStart || target > entry.normEnd)
            continue;
        // 索引建立后节点可能被 splitText 切短，偏移必须以「当前」长度为界。
        // 收敛到节点末尾与指向紧随其后的新节点起点是同一个 DOM 位置，因此这样是安全的。
        const currentLength = entry.node.data.length;
        if (currentLength === 0)
            continue;
        let raw = -1;
        for (let i = 0; i < Math.min(entry.rawLength, currentLength); i += 1) {
            const value = entry.rawToNorm[i];
            if (value >= 0 && value >= target) {
                raw = i;
                break;
            }
        }
        if (raw === -1)
            raw = Math.min(entry.rawLength, currentLength);
        return { node: entry.node, offset: Math.max(0, Math.min(raw, currentLength)) };
    }
    for (let i = index.nodes.length - 1; i >= 0; i -= 1) {
        const entry = index.nodes[i];
        if (entry.node.data.length > 0) {
            return { node: entry.node, offset: entry.node.data.length };
        }
    }
    return null;
}
/** 按句末标点切句，返回每句在归一化文本里的区间。 */
export function sentenceRanges(text) {
    const ranges = [];
    if (text.trim().length === 0)
        return ranges;
    const boundary = /[.!?]["'”’)\]]?(?=\s|$)/g;
    let start = 0;
    let match = boundary.exec(text);
    while (match) {
        const end = match.index + match[0].length;
        if (end > start)
            ranges.push({ start, end });
        let cursor = end;
        while (cursor < text.length && /\s/.test(text[cursor]))
            cursor += 1;
        start = cursor;
        boundary.lastIndex = cursor;
        match = boundary.exec(text);
    }
    if (start < text.length)
        ranges.push({ start, end: text.length });
    return ranges;
}
/** 行内元素白名单：包裹句子后如果落在这类元素里，需要把它提到段落层级。 */
const INLINE_TAGS = new Set([
    'STRONG', 'EM', 'B', 'I', 'U', 'S', 'SPAN', 'A', 'SUP', 'SUB', 'MARK', 'SMALL',
    'CODE', 'ABBR', 'CITE', 'Q', 'DEL', 'INS', 'LABEL', 'FONT', 'BDI', 'BDO', 'DATA',
    'DFN', 'KBD', 'SAMP', 'TIME', 'VAR',
]);
/**
 * 把句子 span 提升到段落层级。
 *
 * 当句子起点落在行内元素内部（例如段首字母写在 <strong>B</strong> 里）时，
 * `extractContents` 会把该行内元素克隆进 fragment，`insertNode` 又把 span 插回
 * 那个（已空的）行内元素里，结果整句都被套上了粗体。这里把 span 提出去并删掉空壳，
 * 保证只影响句子边界、不改变原有排版。
 */
function liftOutOfInlineAncestors(span) {
    let parent = span.parentElement;
    while (parent && INLINE_TAGS.has(parent.tagName)) {
        const onlyContent = (parent.textContent ?? '').trim() === (span.textContent ?? '').trim() &&
            parent.childNodes.length <= 2;
        if (!onlyContent)
            break;
        const grandparent = parent.parentNode;
        if (!grandparent)
            break;
        grandparent.insertBefore(span, parent);
        parent.remove();
        parent = span.parentElement;
    }
}
/** 用 extractContents + insertNode 包裹范围。 */
function wrapRange(range, buildSpan) {
    try {
        const fragment = range.extractContents();
        const span = buildSpan();
        span.appendChild(fragment);
        range.insertNode(span);
        liftOutOfInlineAncestors(span);
        return span;
    }
    catch {
        return null;
    }
}
function decorateBlock(block) {
    if (block.dataset.decorated === 'true')
        return;
    const first = buildTextIndex(block);
    if (first.text.trim().length === 0) {
        block.dataset.decorated = 'true';
        return;
    }
    // 在句界处切开文本节点；从后往前切，前面的切分不会影响后面的定位
    const boundaries = sentenceRanges(first.text)
        .slice(1)
        .map((range) => range.start)
        .reverse();
    for (const boundary of boundaries) {
        const position = positionAt(first, boundary);
        if (!position)
            continue;
        if (position.offset <= 0 || position.offset >= position.node.data.length)
            continue;
        position.node.splitText(position.offset);
    }
    // 逐句包裹。每句都重新建索引：extractContents 会切分/搬移节点，
    // 复用旧索引会把后面的句子定位到错误的位置。
    const sentences = sentenceRanges(buildTextIndex(block).text);
    sentences.forEach((range, index) => {
        const fresh = buildTextIndex(block);
        const startPosition = positionAt(fresh, range.start);
        const endPosition = positionAt(fresh, range.end);
        if (!startPosition || !endPosition) {
            report({
                stage: 'decorate',
                blockId: block.dataset.blockId,
                sentenceIndex: index,
                reason: 'position-not-found',
                detail: `range=[${range.start},${range.end})`,
            });
            return;
        }
        const domRange = document.createRange();
        domRange.setStart(startPosition.node, startPosition.offset);
        domRange.setEnd(endPosition.node, endPosition.offset);
        if (domRange.collapsed) {
            report({
                stage: 'decorate',
                blockId: block.dataset.blockId,
                sentenceIndex: index,
                reason: 'collapsed-range',
                detail: `range=[${range.start},${range.end})`,
            });
            return;
        }
        const span = wrapRange(domRange, () => {
            const element = document.createElement('span');
            element.className = 'sentence';
            element.dataset.sentence = String(index);
            return element;
        });
        if (!span) {
            report({
                stage: 'decorate',
                blockId: block.dataset.blockId,
                sentenceIndex: index,
                reason: 'wrap-failed',
                detail: `range=[${range.start},${range.end})`,
            });
        }
    });
    block.dataset.decorated = 'true';
}
/** 清掉包裹过程中产生的空行内元素外壳（如提取内容后剩下的空 <strong>）。 */
function pruneEmptyInline(root) {
    const selector = Array.from(INLINE_TAGS).join(',');
    root.querySelectorAll(selector).forEach((element) => {
        if (element.classList.contains('sentence'))
            return;
        if (element.classList.contains('anno'))
            return;
        if (element.children.length > 0)
            return;
        if ((element.textContent ?? '').trim().length > 0)
            return;
        element.remove();
    });
}
/** 给文章容器里的每个段落切句并打上句级锚点标记。 */
export function decoratePassage(container) {
    container.querySelectorAll(BLOCK_SELECTOR).forEach((block) => decorateBlock(block));
    pruneEmptyInline(container);
}
function clearMarks(container) {
    container.querySelectorAll('mark.anno').forEach((mark) => {
        const parent = mark.parentNode;
        if (!parent)
            return;
        while (mark.firstChild)
            parent.insertBefore(mark.firstChild, mark);
        parent.removeChild(mark);
    });
    container.querySelectorAll('.note-marker').forEach((marker) => marker.remove());
    container.querySelectorAll('.sentence.has-anno').forEach((sentence) => {
        sentence.classList.remove('has-anno');
    });
    pruneEmptyInline(container);
}
function applyOne(sentence, annotation) {
    const index = buildTextIndex(sentence);
    if (index.text.length === 0)
        return;
    let start = Math.max(0, Math.min(annotation.startOffset, index.text.length));
    let end = Math.max(0, Math.min(annotation.endOffset, index.text.length));
    // 偏移为空或反了，视为「整句高亮」，避免静默什么都不做
    if (end <= start) {
        start = 0;
        end = index.text.length;
    }
    if (end <= start)
        return;
    const startPosition = positionAt(index, start);
    const endPosition = positionAt(index, end);
    if (!startPosition || !endPosition) {
        report({
            stage: 'apply',
            sentenceIndex: Number(sentence.dataset.sentence ?? -1),
            annotationId: annotation.id,
            reason: 'position-not-found',
        });
        return;
    }
    const domRange = document.createRange();
    domRange.setStart(startPosition.node, startPosition.offset);
    domRange.setEnd(endPosition.node, endPosition.offset);
    if (domRange.collapsed) {
        report({
            stage: 'apply',
            sentenceIndex: Number(sentence.dataset.sentence ?? -1),
            annotationId: annotation.id,
            reason: 'collapsed-range',
            detail: `offsets=[${start},${end}) textLength=${index.text.length}`,
        });
        return;
    }
    const mark = wrapRange(domRange, () => {
        const element = document.createElement('mark');
        element.className = 'anno';
        element.dataset.annotationId = annotation.id;
        if (annotation.color)
            element.dataset.color = annotation.color;
        else
            element.classList.add('anno--plain');
        // 有笔记时用下划线提示，**不插入任何额外元素**，避免顶开后面的文字
        if (annotation.note)
            element.classList.add('has-note');
        return element;
    });
    if (!mark) {
        report({
            stage: 'apply',
            sentenceIndex: Number(sentence.dataset.sentence ?? -1),
            annotationId: annotation.id,
            reason: 'wrap-failed',
        });
    }
}
/** 把标注渲染进容器（先清掉旧标记，保证幂等）。文章与题目区共用这一份。 */
export function applyAnnotations(container, annotations) {
    clearMarks(container);
    if (annotations.length === 0)
        return;
    const grouped = new Map();
    for (const annotation of annotations) {
        const key = `${annotation.anchorKind}\u0000${annotation.anchorId}\u0000${annotation.sentenceIndex}`;
        const list = grouped.get(key);
        if (list)
            list.push(annotation);
        else
            grouped.set(key, [annotation]);
    }
    for (const [key, list] of grouped) {
        const [anchorKind, anchorId, sentenceIndex] = key.split('\u0000');
        // 落点种类也要匹配：段落 id（p-1）与题目 id（q14）命名空间不同，
        // 万一将来撞名，也不会把标注画到错的元素上
        const block = container.querySelector(`[data-anchor-kind="${CSS.escape(anchorKind ?? '')}"][data-block-id="${CSS.escape(anchorId ?? '')}"]`);
        const sentence = block?.querySelector(`.sentence[data-sentence="${CSS.escape(sentenceIndex ?? '')}"]`);
        if (!sentence) {
            report({
                stage: 'apply',
                blockId: anchorId,
                sentenceIndex: Number(sentenceIndex),
                reason: 'sentence-element-missing',
                detail: '锚点对应的句子元素不存在，标注无法渲染',
            });
            continue;
        }
        sentence.classList.add('has-anno');
        // 从后往前套用，避免影响同一句里更靠前的偏移
        [...list]
            .sort((a, b) => b.startOffset - a.startOffset)
            .forEach((annotation) => applyOne(sentence, annotation));
    }
    // 套用过程中同样会留下空壳，立即清理，别等到下次重渲染
    pruneEmptyInline(container);
}
/** 一次渲染：写入段落 HTML -> 切句 -> 套用标注。 */
export function renderPassage(container, blocks, annotations) {
    container.innerHTML = blocks
        .map((block) => `<div class="passage-block" data-anchor-kind="passage" data-block-id="${block.id}">${block.html}</div>`)
        .join('');
    decoratePassage(container);
    applyAnnotations(container, annotations);
}
/** 找出某个 DOM 节点所属的文章段落。 */
export function blockOf(node) {
    const element = node.nodeType === Node.TEXT_NODE ? node.parentElement : node;
    return element?.closest(BLOCK_SELECTOR) ?? null;
}
/**
 * 把选区解析成若干「句级片段」。
 * 跨句选择会拆成多条标注（每句一条），这样锚点始终是句级的，不需要绝对偏移。
 */
export function resolveSelectionToParts(block, range) {
    const index = buildTextIndex(block);
    if (index.text.length === 0)
        return [];
    const a = normOffsetOf(index, range.startContainer, range.startOffset);
    const b = normOffsetOf(index, range.endContainer, range.endOffset);
    const from = Math.min(a, b);
    const to = Math.max(a, b);
    if (to <= from)
        return [];
    const parts = [];
    sentenceRanges(index.text).forEach((sentence, sentenceIndex) => {
        const start = Math.max(from, sentence.start);
        const end = Math.min(to, sentence.end);
        if (end <= start)
            return;
        parts.push({
            sentenceIndex,
            startOffset: start - sentence.start,
            endOffset: end - sentence.start,
            text: index.text.slice(start, end),
        });
    });
    return parts;
}
/** 定位某条标注对应的句子元素（用于从列表跳转）。 */
export function findSentenceElement(container, anchorKind, anchorId, sentenceIndex) {
    const block = container.querySelector(`[data-anchor-kind="${CSS.escape(anchorKind)}"][data-block-id="${CSS.escape(anchorId)}"]`);
    return (block?.querySelector(`.sentence[data-sentence="${sentenceIndex}"]`) ?? null);
}
//# sourceMappingURL=annotate.js.map